import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { PhotoGallery } from "../apiPath/api";

export const getAllPhotos = createAsyncThunk(
  "photoGallery/getAllPhotos",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await PhotoGallery.getAllPhotos(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch photos");
    }
  }
);

export const getPhotosByCategory = createAsyncThunk(
  "photoGallery/getPhotosByCategory",
  async ({ categoryId, categoryType, ...params }, { rejectWithValue }) => {
    try {
      const response = await PhotoGallery.getPhotosByCategory({ categoryId, categoryType, ...params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch photos");
    }
  }
);

export const createPhotoGallery = createAsyncThunk(
  "photoGallery/createPhotoGallery",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await PhotoGallery.createPhoto(formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create photo");
    }
  }
);

export const updatePhotoGallery = createAsyncThunk(
  "photoGallery/updatePhotoGallery",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await PhotoGallery.updatePhoto(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update photo");
    }
  }
);

// Add delete and undo delete actions
export const deletePhoto = createAsyncThunk(
  "photoGallery/deletePhoto",
  async (id, { rejectWithValue }) => {
    try {
      const response = await PhotoGallery.deletePhoto(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete photo");
    }
  }
);

export const undoDeletePhoto = createAsyncThunk(
  "photoGallery/undoDeletePhoto",
  async (id, { rejectWithValue }) => {
    try {
      const response = await PhotoGallery.undoDeletePhoto(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to undo delete photo");
    }
  }
);

const photoGallerySlice = createSlice({
  name: "photoGallery",
  initialState: {
    loading: false,
    error: null,
    success: false,
    photos: [],
    pagination: { total: 0, page: 1, limit: 10, totalPages: 1 },
    statusCounts: { all: 0, active: 0, inactive: 0, deleted: 0 }
  },
  reducers: {
    resetPhotoGalleryState: (state) => {
      state.error = null;
      state.success = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllPhotos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllPhotos.fulfilled, (state, action) => {
        state.loading = false;
        state.photos = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.statusCounts = action.payload.statusCounts || state.statusCounts;
      })
      .addCase(getAllPhotos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getPhotosByCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPhotosByCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.photos = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.statusCounts = action.payload.statusCounts || state.statusCounts;
      })
      .addCase(getPhotosByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createPhotoGallery.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPhotoGallery.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(createPhotoGallery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePhotoGallery.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePhotoGallery.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updatePhotoGallery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deletePhoto.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePhoto.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(deletePhoto.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(undoDeletePhoto.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(undoDeletePhoto.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(undoDeletePhoto.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { resetPhotoGalleryState } = photoGallerySlice.actions;
export default photoGallerySlice.reducer;